import React, { useEffect, useState, useMemo } from "react";
import { api, API } from "../lib/api";
import { useSiteContext } from "../components/layout/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { ShieldCheck, FilePlus2, FileText, AlertTriangle, RotateCcw, FlaskConical, Clock, Download, CheckCircle2, XCircle, Lock } from "lucide-react";
import { toast } from "sonner";

const SYSTEMS = ["hydrant", "sprinkler", "booster", "ventilation", "break_tank"];
const COMPONENTS = ["main_pump", "standby_pump", "jockey_pump", "inlet_valve", "outlet_valve", "booster_pump"];

function Section({ icon: Icon, title, subtitle, right, children, testid }) {
  return (
    <section className="glass rounded-lg p-5 space-y-3" data-testid={testid}>
      <div className="flex items-start justify-between flex-wrap gap-2">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-lg f360-gradient flex items-center justify-center"><Icon className="w-5 h-5 text-gray-900" /></div>
          <div>
            <h3 className="font-bold text-base">{title}</h3>
            {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
          </div>
        </div>
        {right}
      </div>
      {children}
    </section>
  );
}

const downloadAuth = async (url, filename) => {
  const token = localStorage.getItem("f360_token");
  const r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!r.ok) { toast.error("Download failed"); return; }
  const blob = await r.blob();
  const u = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = u; a.download = filename; a.click();
  URL.revokeObjectURL(u);
};

// ─────────────────────────────────────────────────────────────
function CountdownPill({ until }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const i = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(i); }, []);
  const ms = new Date(until).getTime() - now;
  if (ms <= 0) return <span className="text-[10px] text-red-700">Reverting…</span>;
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  return <span className="text-xs font-mono tabular-nums text-amber-700">{h.toString().padStart(2, "0")}:{m.toString().padStart(2, "0")}:{s.toString().padStart(2, "0")}</span>;
}

// ─────────────────────────────────────────────────────────────
export default function GRCompliance() {
  const { sites, selectedSite } = useSiteContext();
  const [diesel, setDiesel] = useState({ tests: [], site_status: [] });
  const [overrides, setOverrides] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [certs, setCerts] = useState([]);
  const [showDiesel, setShowDiesel] = useState(false);
  const [showOverride, setShowOverride] = useState(false);

  const reload = async () => {
    try {
      const params = selectedSite && selectedSite !== "all" ? { site_id: selectedSite } : {};
      const [d, o, m, c] = await Promise.all([
        api.get("/diesel-tests", { params }),
        api.get("/manual-overrides", { params }),
        api.get("/monthly-gr-test", { params }),
        api.get("/certificates").catch(() => ({ data: [] })),
      ]);
      // /api/certificates may return {certificates:[...]} or [...] — normalize
      const certList = Array.isArray(c.data) ? c.data : (c.data?.certificates || c.data?.items || []);
      setDiesel(d.data); setOverrides(o.data); setMonthly(m.data); setCerts(certList);
    } catch (e) { /* ignore */ }
  };
  useEffect(() => { reload(); const i = setInterval(reload, 30000); return () => clearInterval(i); }, [selectedSite]); // eslint-disable-line

  const certBySite = useMemo(() => {
    const map = {};
    certs.forEach((c) => {
      if (!map[c.site_id] || (c.valid_until || "") > (map[c.site_id].valid_until || "")) map[c.site_id] = c;
    });
    return map;
  }, [certs]);

  return (
    <div className="space-y-6" data-testid="gr-compliance-page">
      <div>
        <div className="text-xs text-gray-500">Reports → GR Compliance</div>
        <h1 className="text-3xl md:text-4xl font-extrabold flex items-center gap-2"><ShieldCheck className="w-8 h-8 text-orange-400" strokeWidth={2.5} /> GR Compliance Logs</h1>
        <p className="text-sm text-gray-500 mt-1">Diesel weekly test · Manual override audit · Monthly GR test · ACMS certificate status</p>
      </div>

      {/* DIESEL WEEKLY TEST */}
      <Section icon={FlaskConical} title="Diesel Pump Weekly Test Log (GR-02)" subtitle="Submit to Fire Brigade monthly · overdue = no test in last 7 days"
        right={<div className="flex gap-2">
          <Button variant="outline" className="border-gray-200" onClick={() => downloadAuth(`${API}/diesel-tests/export.pdf${selectedSite && selectedSite !== "all" ? `?site_id=${selectedSite}` : ""}`, "diesel-test-log.pdf")} data-testid="diesel-export-btn">
            <Download className="w-4 h-4 mr-1" strokeWidth={2.5} /> Export PDF
          </Button>
          <Button className="f360-gradient border-0" onClick={() => setShowDiesel(true)} data-testid="diesel-add-btn"><FilePlus2 className="w-4 h-4 mr-1" strokeWidth={2.5} /> Record Test</Button>
        </div>}
        testid="diesel-section">
        {/* Overdue sites */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {diesel.site_status.map((s) => (
            <div key={s.site_id} className={`rounded-lg p-3 ${s.overdue ? "bg-red-500/10 border border-red-500/30" : "bg-emerald-500/10 border border-emerald-500/30"}`} data-testid={`diesel-status-${s.site_id}`}>
              <div className="text-xs font-bold">{s.name}</div>
              <div className="text-[10px] text-gray-500 mt-0.5">Last test: <span className="text-gray-900">{s.last_test ? s.last_test.slice(0, 10) : "Never"}</span></div>
              <div className={`text-[10px] mt-0.5 font-bold ${s.overdue ? "text-red-700" : "text-emerald-700"}`}>{s.overdue ? `OVERDUE · ${s.days_since ?? "—"}d` : `On schedule · ${s.days_since}d ago`}</div>
            </div>
          ))}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr><th className="text-left py-2">Date</th><th className="text-left">Site</th><th className="text-right">Run (min)</th><th className="text-right">Pressure</th><th>Tested By</th><th>Result</th></tr>
            </thead>
            <tbody>
              {diesel.tests.length === 0 && <tr><td colSpan={6} className="text-center text-gray-500 py-4">No tests recorded yet.</td></tr>}
              {diesel.tests.map((t) => (
                <tr key={t.test_id} className="border-b border-gray-200">
                  <td className="py-1.5">{(t.test_date || "").slice(0, 10)}</td>
                  <td>{sites.find((s) => s.site_id === t.site_id)?.name || t.site_id}</td>
                  <td className="text-right">{t.run_duration_min}</td>
                  <td className="text-right">{t.pressure_achieved_bar?.toFixed?.(2)} bar</td>
                  <td className="text-xs text-gray-500">{t.tested_by}</td>
                  <td>{t.passed ? <span className="text-emerald-700 font-bold">PASS</span> : <span className="text-red-700 font-bold">FAIL</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* MANUAL OVERRIDE LOG */}
      <Section icon={Lock} title="Manual Override Log (GR-03)" subtitle="AUTO → MANUAL switches · 4-hour auto-revert · all entries mirrored to Audit Log"
        right={<Button className="f360-gradient border-0" onClick={() => setShowOverride(true)} data-testid="override-add-btn"><FilePlus2 className="w-4 h-4 mr-1" strokeWidth={2.5} /> Start Override</Button>}
        testid="override-section">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr><th className="text-left py-2">Started</th><th className="text-left">Site</th><th>System</th><th>Component</th><th>Operator</th><th>Reason</th><th>Status</th><th>Countdown</th><th></th></tr>
            </thead>
            <tbody>
              {overrides.length === 0 && <tr><td colSpan={9} className="text-center text-gray-500 py-4">No overrides logged.</td></tr>}
              {overrides.map((o) => (
                <tr key={o.override_id} className="border-b border-gray-200" data-testid={`override-row-${o.override_id}`}>
                  <td className="py-1.5 text-xs">{new Date(o.started_at).toLocaleString("en-IN")}</td>
                  <td>{sites.find((s) => s.site_id === o.site_id)?.name || o.site_id}</td>
                  <td className="text-xs uppercase">{o.system}</td>
                  <td className="text-xs">{o.component}</td>
                  <td className="text-xs">{o.operator_name}</td>
                  <td className="text-xs text-gray-500 truncate max-w-[200px]" title={o.reason}>{o.reason}</td>
                  <td>{o.active ? <span className="text-amber-700 font-bold text-xs">ACTIVE</span> : <span className="text-gray-500 text-xs">{o.reverted_by === "system_auto_revert" ? "AUTO-REVERTED" : "REVERTED"}</span>}</td>
                  <td>{o.active ? <CountdownPill until={o.auto_revert_at} /> : <span className="text-[10px] text-gray-500">—</span>}</td>
                  <td>{o.active && <button onClick={async () => { await api.post(`/manual-overrides/${o.override_id}/revert`); toast.success("Reverted"); reload(); }} className="text-xs text-orange-700 hover:text-gray-900" data-testid={`revert-${o.override_id}`}><RotateCcw className="w-3.5 h-3.5 inline mr-1" strokeWidth={2.5} />Revert</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* MONTHLY GR TEST */}
      <Section icon={ShieldCheck} title="Monthly GR Test (GR-04)" subtitle="Simulate pressure drop → pump auto-start → generate compliance certificate"
        testid="monthly-section">
        <div className="flex flex-wrap gap-2 mb-2">
          {(selectedSite && selectedSite !== "all" ? [selectedSite] : sites.map((s) => s.site_id)).slice(0, 3).map((sid) => {
            const site = sites.find((s) => s.site_id === sid);
            return ["hydrant", "sprinkler"].map((sys) => (
              <Button key={`${sid}-${sys}`} variant="outline" className="border-gray-200 text-xs" data-testid={`run-monthly-${sys}-${sid}`}
                onClick={async () => {
                  try { const r = await api.post("/monthly-gr-test", { site_id: sid, system: sys, simulated: true });
                    toast.success(`${sys.toUpperCase()} test: ${r.data.passed ? "PASS" : "FAIL"} · cert ${r.data.cert_no}`); reload(); }
                  catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
                }}>
                Run {sys.toUpperCase()} @ {site?.name?.slice(0, 18)}
              </Button>
            ));
          })}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <tr><th className="text-left py-2">Date</th><th>Cert No</th><th>Site</th><th>System</th><th>Response</th><th>Result</th><th></th></tr>
            </thead>
            <tbody>
              {monthly.length === 0 && <tr><td colSpan={7} className="text-center text-gray-500 py-4">No monthly tests yet. Click a button above to run one.</td></tr>}
              {monthly.map((t) => (
                <tr key={t.test_id} className="border-b border-gray-200">
                  <td className="py-1.5 text-xs">{new Date(t.test_date).toLocaleString("en-IN")}</td>
                  <td className="font-mono text-xs">{t.cert_no}</td>
                  <td className="text-xs">{sites.find((s) => s.site_id === t.site_id)?.name || t.site_id}</td>
                  <td className="text-xs uppercase">{t.system}</td>
                  <td className="text-xs">{t.pump_response_sec}s</td>
                  <td>{t.passed ? <span className="text-emerald-700 font-bold text-xs">PASS</span> : <span className="text-red-700 font-bold text-xs">FAIL</span>}</td>
                  <td><button onClick={() => downloadAuth(`${API}/monthly-gr-test/${t.test_id}/cert.pdf`, `${t.cert_no}.pdf`)} className="text-xs text-orange-700 hover:text-gray-900" data-testid={`cert-pdf-${t.test_id}`}><Download className="w-3.5 h-3.5 inline mr-1" strokeWidth={2.5} />Cert PDF</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>

      {/* ACMS CERT STATUS PER SITE (GR-01) */}
      <Section icon={FileText} title="ACMS Certificate Status (GR-01)" subtitle="Latest certificate per site · click to view PDF"
        testid="acms-cert-section">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {sites.length === 0 && <div className="text-gray-500 text-sm">No sites.</div>}
          {sites.map((s) => {
            const c = certBySite[s.site_id];
            const now = Date.now();
            const validUntil = c?.valid_until ? new Date(c.valid_until).getTime() : 0;
            const expired = c && validUntil < now;
            const expiringSoon = c && !expired && validUntil - now < 30 * 86400000;
            const cls = !c ? "bg-slate-500/10 border-slate-500/30" : expired ? "bg-red-500/10 border-red-500/30" : expiringSoon ? "bg-amber-500/10 border-amber-500/30" : "bg-emerald-500/10 border-emerald-500/30";
            return (
              <div key={s.site_id} className={`rounded-lg p-3 border ${cls}`} data-testid={`cert-site-${s.site_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <div className="font-bold text-sm">{s.name}</div>
                    <div className="text-[10px] text-gray-500 mt-0.5">{c?.certificate_no || "No certificate"}</div>
                  </div>
                  {c ? (expired ? <XCircle className="w-5 h-5 text-red-400" strokeWidth={2.5} /> : <CheckCircle2 className="w-5 h-5 text-emerald-400" strokeWidth={2.5} />) : <AlertTriangle className="w-5 h-5 text-gray-500" strokeWidth={2.5} />}
                </div>
                {c && <>
                  <div className="text-[10px] mt-2 text-gray-500">Valid until: <b className="text-gray-900">{(c.valid_until || "").slice(0, 10)}</b></div>
                  <div className={`text-[10px] mt-0.5 font-bold ${expired ? "text-red-700" : expiringSoon ? "text-amber-700" : "text-emerald-700"}`}>
                    {expired ? "EXPIRED · renew NOW" : expiringSoon ? `Expiring in ${Math.ceil((validUntil - now) / 86400000)} days` : "VALID"}
                  </div>
                </>}
              </div>
            );
          })}
        </div>
      </Section>

      {/* DIALOGS */}
      <DieselDialog open={showDiesel} onClose={() => setShowDiesel(false)} sites={sites} preSite={selectedSite} onSaved={reload} />
      <OverrideDialog open={showOverride} onClose={() => setShowOverride(false)} sites={sites} preSite={selectedSite} onSaved={reload} />
    </div>
  );
}

// ───── DIALOGS ───────────────────────────────────────────────────────
function DieselDialog({ open, onClose, sites, preSite, onSaved }) {
  const [f, setF] = useState({ site_id: "", test_date: new Date().toISOString().slice(0, 10), run_duration_min: 10, pressure_achieved_bar: 3.8, tested_by: "", remarks: "" });
  useEffect(() => { if (open) setF((p) => ({ ...p, site_id: (preSite && preSite !== "all" ? preSite : sites[0]?.site_id) || "", test_date: new Date().toISOString().slice(0, 10) })); }, [open, preSite, sites]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border border-gray-200 text-gray-900 max-w-lg">
        <DialogHeader><DialogTitle>Record Diesel Weekly Test</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Site</span>
            <Select value={f.site_id} onValueChange={(v) => setF({ ...f, site_id: v })}>
              <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="dt-site"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></label>
          <label><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Test Date</span><Input type="date" value={f.test_date} onChange={(e) => setF({ ...f, test_date: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="dt-date" /></label>
          <label><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Run Duration (min)</span><Input type="number" value={f.run_duration_min} onChange={(e) => setF({ ...f, run_duration_min: Number(e.target.value) })} className="bg-gray-50 border-gray-200" data-testid="dt-duration" /></label>
          <label className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Pressure Achieved (bar)</span><Input type="number" step="0.01" value={f.pressure_achieved_bar} onChange={(e) => setF({ ...f, pressure_achieved_bar: Number(e.target.value) })} className="bg-gray-50 border-gray-200" data-testid="dt-pressure" /></label>
          <label className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Tested By</span><Input value={f.tested_by} onChange={(e) => setF({ ...f, tested_by: e.target.value })} placeholder="Operator name" className="bg-gray-50 border-gray-200" /></label>
          <label className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Remarks</span><Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} className="bg-gray-50 border-gray-200" /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gray-200">Cancel</Button>
          <Button className="f360-gradient border-0" data-testid="dt-save" onClick={async () => {
            try { await api.post("/diesel-tests", f); toast.success("Test recorded"); onClose(); onSaved(); }
            catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
          }}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverrideDialog({ open, onClose, sites, preSite, onSaved }) {
  const [f, setF] = useState({ site_id: "", system: "hydrant", component: "main_pump", operator_name: "", reason: "", duration_minutes: 240 });
  useEffect(() => { if (open) setF((p) => ({ ...p, site_id: (preSite && preSite !== "all" ? preSite : sites[0]?.site_id) || "", operator_name: "", reason: "" })); }, [open, preSite, sites]);
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white border border-gray-200 text-gray-900 max-w-lg">
        <DialogHeader><DialogTitle>Start Manual Override</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Site</span>
            <Select value={f.site_id} onValueChange={(v) => setF({ ...f, site_id: v })}>
              <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="ov-site"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">{sites.map((s) => <SelectItem key={s.site_id} value={s.site_id}>{s.name}</SelectItem>)}</SelectContent>
            </Select></label>
          <label><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">System</span>
            <Select value={f.system} onValueChange={(v) => setF({ ...f, system: v })}>
              <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="ov-system"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">{SYSTEMS.map((s) => <SelectItem key={s} value={s}>{s.toUpperCase()}</SelectItem>)}</SelectContent>
            </Select></label>
          <label><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Component</span>
            <Select value={f.component} onValueChange={(v) => setF({ ...f, component: v })}>
              <SelectTrigger className="bg-gray-50 border-gray-200" data-testid="ov-component"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white border-gray-200 text-gray-900">{COMPONENTS.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select></label>
          <label className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Operator Name *</span><Input value={f.operator_name} onChange={(e) => setF({ ...f, operator_name: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="ov-name" /></label>
          <label className="col-span-2"><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Reason *</span><Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} className="bg-gray-50 border-gray-200" data-testid="ov-reason" /></label>
          <label><span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Auto-revert in (minutes)</span><Input type="number" min="1" max="720" value={f.duration_minutes} onChange={(e) => setF({ ...f, duration_minutes: Number(e.target.value) })} className="bg-gray-50 border-gray-200" data-testid="ov-duration" /></label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-gray-200">Cancel</Button>
          <Button className="f360-gradient border-0" data-testid="ov-save" onClick={async () => {
            if (!f.operator_name || !f.reason) { toast.error("Operator name + reason required"); return; }
            try { await api.post("/manual-overrides", f); toast.success("Override started"); onClose(); onSaved(); }
            catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
          }}>Start Override</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
